import jwt_decode from 'jwt-decode';
import { getUnixTime } from 'date-fns';
import type { AuthState } from '../../model/auth.types';
import { Permission, UserType } from '../../model/auth.types';

export type PostLoginResponse = {
    jwt: string;
    refreshCode: string;
    longLivedToken: string;
    validFor: number;
};

let refreshTimeOut: ReturnType<typeof setTimeout> | null = null;

export type StartRefreshCycleParams = {
    refreshCode: string;
    username: string;
    timeLeft: number; // time out in ms
    onRefresh: (refreshCode: string) => Promise<PostLoginResponse>;
    onSuccess: (data: AuthState) => void;
    onError: () => void;
};

export const clearRefreshCycle = () => {
    if (refreshTimeOut) {
        clearTimeout(refreshTimeOut);
        refreshTimeOut = null;
    }
};

export const startRefreshCycle = async ({
    refreshCode,
    username,
    timeLeft,
    onRefresh,
    onSuccess,
    onError,
}: StartRefreshCycleParams) => {
    // Clear any existing timeout first
    clearRefreshCycle();

    // Validate parameters
    if (!refreshCode || !username || timeLeft <= 0) {
        console.warn('Invalid refresh cycle parameters', {
            refreshCode: !!refreshCode,
            username: !!username,
            timeLeft,
        });
        return;
    }

    refreshTimeOut = setTimeout(async () => {
        refreshTimeOut = null;
        try {
            const data = await onRefresh(refreshCode);
            const storeData = extractAuthDataFromAuthResponse(data, username);
            onSuccess(storeData);

            startRefreshCycle({
                refreshCode: storeData.refreshCode,
                username,
                timeLeft: Math.max(0, (storeData.validFor - 180) * 1000),
                onRefresh,
                onSuccess,
                onError,
            });
        } catch (e) {
            console.error(e);
            onError();
        }
    }, timeLeft);
};

export const extractAuthDataFromAuthResponse = (
    data: PostLoginResponse,
    username: string,
    options?: {
        tokenExpiresAtOffset?: number; // Direct offset in seconds (e.g., validFor - 5)
        tokenExpiresAtMultiplier?: number; // Multiplier for validFor (e.g., 0.5 for validFor / 2)
        usePermissionsFromJwt?: boolean; // If true, use permissions from JWT, otherwise derive from userType
    }
): AuthState => {
    const { jwt, refreshCode, validFor } = data;

    const decoded: {
        sub: string;
        iat: number;
        g: [UserType, string?];
        p?: Permission[];
    } = jwt_decode(jwt);

    const [userType, lOId] = decoded.g;

    const userId = decoded.sub;
    let liftOperatorId = decoded.sub;
    if (userType === UserType.LiftOperatorUser || userType === UserType.B2B) {
        liftOperatorId = lOId?.split(':')[1] || decoded.sub;
    }

    // Calculate tokenExpiresAt
    // If tokenExpiresAtOffset is provided, use it; otherwise use validFor
    // For B2B: validFor / 2, for admin: validFor, for backoffice: validFor - 5
    let tokenExpiresAtOffset: number;
    if (options?.tokenExpiresAtOffset !== undefined) {
        tokenExpiresAtOffset = options.tokenExpiresAtOffset;
    } else if (options?.tokenExpiresAtMultiplier !== undefined) {
        tokenExpiresAtOffset = validFor * options.tokenExpiresAtMultiplier;
    } else {
        tokenExpiresAtOffset = validFor;
    }
    const tokenExpiresAt = getUnixTime(new Date()) + tokenExpiresAtOffset;

    let permissions: Permission[];
    if (options?.usePermissionsFromJwt && decoded.p) {
        permissions = decoded.p;
    } else {
        // Derive permissions from userType (backoffice behavior)
        // If permissions are in JWT, use them; otherwise default to ADMIN
        permissions = decoded.p || [Permission.ADMIN];
    }

    return {
        validFor,
        refreshCode,
        permissions,
        tokenExpiresAt,
        jwt,
        userId,
        userType,
        liftOperatorData: {
            skiOperator: username,
            liftOperatorId,
        },
        logoutType: null,
    };
};
